import { redirect } from "react-router";
import { MEAL_GROUPS } from "src/constants";
import { hotelsApi } from "src/services/api";
import { doMoneyMultiplication, doPriceAddition } from "src/utils/money-utils";
import {
  buildSearchModelFromTravelPackage,
  buildSearchQuery,
  rebuildHotelResultsQueryFromData,
} from "src/utils/query-utils";
import { getPriceAfterFee } from "src/utils/results-utils";
import { isLangAbsent, redirectWithLang } from "./utils/loader-utils";
import { ERROR_STATUS_MESSAGE } from "src/assets/lang/errorMessages";
import { getSelectedLocale } from "src/utils/storage-utils";

function sortByMealType(rates = []) {
  function getMealTypeOrder(type) {
    switch (type) {
      case "breakfast":
        return 1;
      case "breakfastAndSecond":
        return 2;
      case "threeMeals":
        return 3;
      case "allIncluded":
        return 4;
      default:
        return 0;
    }
  }

  return rates.sort((a, b) => {
    const aMealGroup =
      Object.entries(MEAL_GROUPS).find(([_, v]) => v.includes(a.meal))?.[0] || [];
    const bMealGroup =
      Object.entries(MEAL_GROUPS).find(([_, v]) => v.includes(b.meal))?.[0] || [];
    return getMealTypeOrder(aMealGroup) - getMealTypeOrder(bMealGroup);
  });
}

export class HotelPrebookDetails {
  static getSplitPrice(rate, groupSize = 1) {
    const deposit = rate.payment_options?.payment_types?.find((p) => p.type === "deposit");
    if (!deposit) return null;
    return Math.round(deposit.show_amount / groupSize);
  }

  static getExtraTaxes(rate) {
    const deposit = rate.payment_options?.payment_types?.find((p) => p.type === "deposit");
    if (!deposit) return null;

    return deposit.tax_data?.taxes
      ?.filter((t) => !t.included_by_supplier)
      .reduce((totalByCurrency, tax) => {
        totalByCurrency[tax.currency_code] = doPriceAddition(
          totalByCurrency[tax.currency_code] || 0,
          parseFloat(tax.amount),
        );
        return totalByCurrency;
      }, {});
  }

  static normalizeRoomAmenities(roomGroup, roomList) {
    const amenities = new Set(roomGroup?.room_amenities || []);
    roomList.forEach((rate) => {
      (rate.amenities_data || []).forEach((amenity) => amenities.add(amenity));
    });
    return Array.from(amenities).filter(Boolean).sort();
  }

  static getRoomSize(roomGroup, firstRate) {
    if (roomGroup?.size) return roomGroup.size;
    const roomArea = firstRate?.rg_ext?.room_area;
    if (Array.isArray(roomArea) && roomArea.length > 0) return roomArea.join("-");
    return null;
  }

  /**
   * Group hotel rates by unique room type and sort by price within each group
   * @param {Object} hotel - Hotel object with rates array
   * @returns {Array} Array of grouped room types with sorted rates
   * Each group: { roomType: string, roomName: string, capacity: number, rates: Array }
   */
  static getRooms(hotel, groupSize = 1, flightPriceTotal = 0, hotelMarkupTotal = 0) {
    const rates = hotel.rates || [];
    if (!rates || rates.length === 0) {
      return [];
    }

    const roomTypeMap = Map.groupBy(rates, ({ rg_ext }) =>
      Object.entries(rg_ext)
        .map(([k, v]) => `${k}:${v}`)
        .sort()
        .join(","),
    );

    const groupedRooms = Array.from(roomTypeMap.entries()).map(([_, roomList]) => {
      const roomGroup = hotel.room_groups.find(
        (rg) =>
          Object.keys(rg.rg_ext).every((k) => rg.rg_ext[k] === roomList[0].rg_ext[k]) &&
          Object.keys(rg.rg_ext).length === Object.keys(roomList[0].rg_ext).length,
      );

      if (!roomGroup)
        console.error("Room groups field 'hotel.room_groups' not found in hotel data");

      roomList = sortByMealType(roomList);
      roomList = roomList.filter((r) =>
        r.payment_options.payment_types.find((p) => p.type === "deposit"),
      );

      roomList.forEach((room) => {
        room.priceTotal = HotelPrebookDetails.getSplitPrice(room);
        room.pricePerPerson = doMoneyMultiplication(room.priceTotal, 1 / groupSize);
        room.finalTourPriceWithMarkup = doPriceAddition(
          doPriceAddition(room.priceTotal, flightPriceTotal),
          hotelMarkupTotal,
        );
        room.taxByCurrency = HotelPrebookDetails.getExtraTaxes(room);
      });

      return {
        roomType:
          roomGroup?.name_struct?.main_name ||
          roomList[0]?.room_data_trans?.main_name ||
          roomList[0]?.room_data_trans?.main_room_name ||
          roomList[0]?.room_name,
        bedType: roomGroup?.name_struct?.bedding_type || roomList[0]?.room_data_trans?.bedding_type,
        bathroomType: roomGroup?.name_struct?.bathroom || roomList[0]?.room_data_trans?.bathroom,
        areaSize: HotelPrebookDetails.getRoomSize(roomGroup, roomList[0]),
        amenities: HotelPrebookDetails.normalizeRoomAmenities(roomGroup, roomList),
        // Oshibki 3 letter: room photos did not load. Some room groups come
        // back without images, so fall back to the hotel-level photos to keep
        // the room gallery populated.
        images:
          roomGroup?.images?.length > 0
            ? roomGroup.images
            : hotel.images?.length > 0
              ? hotel.images
              : [],
        items: roomList,
      };
    });

    return groupedRooms;
  }
}

class HotelPrebookMapper {
  static toUI(data, props) {
    const numPeople = Object.values(data.guests).reduce((a, b) => a + parseInt(b), 0);

    const bestFlightPrice = getPriceAfterFee(data.includedFlight);
    const flightPriceTotal = doMoneyMultiplication(bestFlightPrice, numPeople);
    const hotelMarkupTotal = Number(data.hotelMarkupTotal) > 0 ? data.hotelMarkupTotal : 0;

    data.rooms = HotelPrebookDetails.getRooms(
      data.hotel,
      numPeople,
      flightPriceTotal,
      hotelMarkupTotal,
    );
    ["latitude", "longitude", "review_rating"].forEach(
      (k) => (data.hotel[k] = parseFloat(data.hotel[k])),
    );
    data.hotel.star_rating = parseInt(data.hotel.star_rating);

    ["amenity_groups", "serp_filters", "images"].forEach((k) => {
      if (!data.hotel[k] || !Array.isArray(data.hotel[k])) {
        data.hotel[k] = [];
      }
    });

    if (!data.nearby || !Array.isArray(data.nearby)) {
      data.nearby = [];
    } else {
      data.nearby.forEach((item) => {
        item.distanceUnit = item.distance < 1000 ? "m" : "km";
        item.distance =
          item.distance < 1000 ? item.distance : (item.distance / 1000).toFixed(2);
      });
    }

    data.reviews.sort((a, b) => new Date(b.created) - new Date(a.created));

    const bestRoom = data.rooms
      .flatMap((r) => r.items)
      .reduce((min, b) =>
        (min.finalTourPriceWithMarkup || b.finalTourPriceWithMarkup) <
        b.finalTourPriceWithMarkup
          ? min
          : b,
      );

    data.minPriceTotal = bestRoom.finalTourPriceWithMarkup;
    data.bestRoomTax = bestRoom.taxByCurrency;
    data.minPricePP = doMoneyMultiplication(data.minPriceTotal, 1 / numPeople);
    data.minFlightPrice = bestFlightPrice;
    data.flightKey = data.includedFlight.mergeKey;
    data.returnUrl = props.returnUrl || "/";

    return data;
  }
}

async function getHotelPrebookData(hid, srkey) {
  const currentLang = getSelectedLocale().split("_")[0].toUpperCase();
  const { data } = await hotelsApi.getHotelPreview(hid, srkey).catch((err) => {
    console.log(err);
    if (err.status === 404) {
      throw Response.json({
        data: err,
        statusText: ERROR_STATUS_MESSAGE[currentLang.toLowerCase()].service,
      });
    }
    // Handle expired search results (410 Gone) and other API errors
    if (err.status === 410) {
      throw Response.json(
        {
          data: err,
          statusText: ERROR_STATUS_MESSAGE[currentLang.toLowerCase()].expired,
        },
        { status: 410 },
      );
    }
    throw Response.json({
      data: err,
      statusText: ERROR_STATUS_MESSAGE[currentLang.toLowerCase()].service,
    });
  });

  return data;
}

export async function loader({ request, params }) {
  const url = new URL(request.url);
  if (!url.search) {
    return redirect("/hotels");
  }
  if (isLangAbsent(request)) return redirectWithLang(request);

  let returnUrl = decodeURIComponent(url.searchParams.get("returnUrl") || "");
  if (!returnUrl.startsWith("/search/hotels")) returnUrl = "";

  // handle not having search req key
  let hasRequiredParamsForSearch = ["guests", "start", "end", "src", "dest"].every((p) =>
    url.searchParams.get(p),
  );
  const srkey = url.searchParams.get("srkey");
  if (!srkey) {
    if (returnUrl) return redirect(returnUrl);
    returnUrl = "/hotels";
    if (hasRequiredParamsForSearch) {
      returnUrl =
        "/search/hotels" +
        rebuildHotelResultsQueryFromData(Array.from(url.searchParams.entries()));
    }

    return redirect(returnUrl);
  }

  let data;
  try {
    data = await getHotelPrebookData(params.id, srkey);
  } catch (err) {
    // Oshibki 5 letter item 10: cheap-offer cards on the home page outlive
    // their search request key (offer cache 24h vs srkey TTL 30 min). Instead
    // of a dead-end 410 page, re-run the same search so the user lands on
    // fresh results with a valid key.
    if (err?.status === 410 && hasRequiredParamsForSearch) {
      return redirect(
        "/search/hotels" +
          rebuildHotelResultsQueryFromData(Array.from(url.searchParams.entries())),
      );
    }
    throw err;
  }
  if (!returnUrl) {
    if (hasRequiredParamsForSearch) {
      returnUrl =
        "/search/hotels" +
        rebuildHotelResultsQueryFromData(Array.from(url.searchParams.entries()));
    } else {
      const searchModel = buildSearchModelFromTravelPackage(data.includedFlight, {
        adult: data.guests.adults,
        child: data.guests.children,
        infant: data.guests.infants,
      });
      returnUrl = "/search/hotels?" + buildSearchQuery(searchModel);
    }
  }

  if (!data.includedFlight.mergeKey) {
    console.error("No flight key found");
    return redirect(returnUrl);
  }

  data = HotelPrebookMapper.toUI(data, { returnUrl });

  console.log(data);
  return data;
}
